/*
 * Copyright (C) 2026 Das Land Schleswig-Holstein vertreten durch den
 * Ministerpräsidenten des Landes Schleswig-Holstein
 * Staatskanzlei
 * Abteilung Digitalisierung und zentrales IT-Management der Landesregierung
 *
 * Lizenziert unter der EUPL, Version 1.2 oder - sobald
 * diese von der Europäischen Kommission genehmigt wurden -
 * Folgeversionen der EUPL ("Lizenz");
 * Sie dürfen dieses Werk ausschließlich gemäß
 * dieser Lizenz nutzen.
 * Eine Kopie der Lizenz finden Sie hier:
 *
 * https://joinup.ec.europa.eu/collection/eupl/eupl-text-eupl-12
 *
 * Sofern nicht durch anwendbare Rechtsvorschriften
 * gefordert oder in schriftlicher Form vereinbart, wird
 * die unter der Lizenz verbreitete Software "so wie sie
 * ist", OHNE JEGLICHE GEWÄHRLEISTUNG ODER BEDINGUNGEN -
 * ausdrücklich oder stillschweigend - verbreitet.
 * Die sprachspezifischen Genehmigungen und Beschränkungen
 * unter der Lizenz sind dem Lizenztext zu entnehmen.
 */
import { NavigationService } from '@alfa-client/navigation-shared';
import { AbstractFormService, createEmptyStateResource, StateResource, tapOnLoadedResource } from '@common/tech-shared';
import { CommonModule } from '@angular/common';
import { Component, ElementRef, inject, Input } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Resource } from '@ngxp/rest';
import { ButtonWithSpinnerComponent } from '@ods/component';
import { focusFirstInvalidField } from '@ods/system';
import { Observable, of, tap } from 'rxjs';
import { ADMINISTRATION_FORMSERVICE } from '../token';

@Component({
  selector: 'administration-save-button',
  imports: [CommonModule, ButtonWithSpinnerComponent],
  templateUrl: './save-button.component.html',
})
export class SaveButtonComponent {
  @Input() successLinkPath: string;
  @Input() isRelativePath: boolean = false;
  @Input() label: string = 'Speichern';

  private readonly formService: AbstractFormService<Resource> = inject(ADMINISTRATION_FORMSERVICE);
  private readonly navigationService: NavigationService = inject(NavigationService);
  private readonly route: ActivatedRoute = inject(ActivatedRoute);
  private readonly elementRef: ElementRef<HTMLElement> = inject(ElementRef);

  public stateResource$: Observable<StateResource<Resource>> = of(createEmptyStateResource<Resource>());

  public submit(): void {
    this.stateResource$ = this.formService.submit().pipe(
      tap((stateResource: StateResource<Resource>) => this._focusFirstInvalidField(stateResource)),
      tapOnLoadedResource((stateResource: StateResource<Resource>) => this._doNavigation(stateResource.resource)),
    );
  }

  _focusFirstInvalidField(stateResource: StateResource<Resource>): void {
    if (stateResource.loading || !this.formService.isInvalid()) return;

    const form: HTMLFormElement = this.elementRef.nativeElement.closest('form');
    if (form) focusFirstInvalidField(form);
  }

  _doNavigation(resource: Resource): void {
    if (!this.successLinkPath) return;
    if (this.isRelativePath) this.navigationService.navigateRelativeTo(this.successLinkPath, this.route);
    else this.navigationService.navigateByResource(this.successLinkPath, resource);
  }
}
